import '@ant-design/v5-patch-for-react-19'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Analytics } from '@vercel/analytics/react'
import './index.css'
import App from './App.tsx'
import { ThemeProvider } from '@/components/theme/ThemeProvider'
import log from '@/lib/logger'
import { startLogTransport } from '@/lib/logTransport'
import { initSentry, Sentry } from '@/lib/sentry'
import { mountAgentSurface } from '@/webmcp/mount'
import { initPostHog } from '@/lib/posthog'
import { setupBudgetPacingPersistence } from '@/lib/budgetPacingPersist'

// Initialize Sentry FIRST (before any errors can occur)
initSentry()

// Real-user performance measurement. No-op unless VITE_POSTHOG_KEY is set,
// and the SDK is only fetched when it is.
initPostHog()

const clientLogTransportEnabled =
  import.meta.env.DEV || import.meta.env.VITE_CLIENT_LOG_TRANSPORT_ENABLED === 'true'

// Core's own WebMCP surface, installed at module scope for the document's
// lifetime. Not a React effect: see the header of src/webmcp/install.ts.
mountAgentSurface()

// Start frontend log transport only when a dev server endpoint exists.
if (clientLogTransportEnabled) {
  startLogTransport()
}

// Global error handlers for Vercel logs + Sentry
window.onerror = (message, source, lineno, colno, error) => {
  log.error('Uncaught error', { message, source, lineno, colno, stack: error?.stack })
  if (error) {
    Sentry.captureException(error)
  }
  return false // Don't suppress default handling
}

window.onunhandledrejection = (event) => {
  log.error('Unhandled promise rejection', {
    reason: event.reason?.message || event.reason,
    stack: event.reason?.stack,
  })
  Sentry.captureException(event.reason)
}

log.info('App starting', {
  env: import.meta.env.MODE,
  logLevel: import.meta.env.VITE_LOG_LEVEL || 'default',
})

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Budgets page warm cache: the slow pacing queries survive a reload and paint
// the last known numbers while they refetch (src/lib/budgetPacingPersist.ts).
setupBudgetPacingPersistence(queryClient)

const enableQueryDevtools = import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === 'true'

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
      <Analytics />
      {enableQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
)
